<template>
  <v-row class="ma-0 pa-0 pb-2">
    <v-col
      order="2"
      order-sm="2"
      order-md="1"
      order-lg="1"
      order-xl="1"
      cols="12"
      sm="12"
      md="8"
      lg="8"
      xl="8"
      class="pl-0"
    >
      <v-card class="py-4">
        <div class="absolute-right">
          <v-menu data-testid="profile-recognitions-filters">
            <template v-slot:activator="{ on }">
              <v-btn plain v-on="on">
                {{ getFilterText }}
                <v-icon color="blue-grey">mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item @click="handleFilterClick('all')">
                {{ $t('recognitions.all_recognitions') }}
              </v-list-item>
              <v-list-item @click="handleFilterClick('given')">
                {{ $t('recognitions.given') }}
              </v-list-item>
              <v-list-item @click="handleFilterClick('received')">
                {{ $t('recognitions.received') }}
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
        <RecognitionsList
          hideActions
          class="pt-2"
          data-testid="profile-recognitions-list"
          section="profile-recognitions-list"
          :loading="loading"
          :recognitions="recognitions"
          :totalPages="totalPages"
          :currentPage="currentPage"
          @pageChange="handlePageChange"
        />
      </v-card>
    </v-col>
    <v-col
      order="1"
      order-sm="1"
      order-md="2"
      order-lg="2"
      order-xl="2"
      cols="12"
      sm="12"
      md="4"
      lg="4"
      xl="4"
      class="pr-0"
    >
      <AccountSummary
        hideAccountInfo
        data-testid="profile-recognitions-accountSummary"
        section="profile-recognitions-section"
        :account="profileAccount"
        :canCreateRecognition="canGiveRecognition"
        @created="fetchRecognitions"
      />
    </v-col>
  </v-row>
</template>

<script>
import { isEmpty } from 'lodash'

import AccountSummary from '../Recognitions/AccountSummary.vue'
import RecognitionsList from '../Recognitions/RecognitionsList.vue'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

const ITEMS_PER_PAGE = 5

export default {
  components: {
    AccountSummary,
    RecognitionsList,
  },
  mixins: [mixin, trackerMixin],
  props: {
    organizationId: {
      required: true,
      type: String,
    },
    profileAccount: {
      type: Object,
      required: true,
    },
    isSelfProfile: {
      type: Boolean,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      recognitions: [],
      totalPages: 0,
      currentPage: 1,
      filters: {},
      ITEMS_PER_PAGE,
    }
  },
  created() {
    this.fetchRecognitions()
  },
  watch: {
    profileAccount: {
      handler() {
        this.fetchRecognitions()
      },
      deep: true,
    },
    isLoading: {
      handler(value) {
        if (!value) return
        this.loading = true
      },
    },
  },
  computed: {
    getFilterText() {
      if (this.filters.createdBy) {
        return this.$t('recognitions.given')
      }
      if (this.filters.accounts) {
        return this.$t('recognitions.received')
      }
      return this.$t('recognitions.all_recognitions')
    },
    canGiveRecognition() {
      return !this.isSelfProfile
    },
  },
  methods: {
    handleFilterClick(filterType) {
      this.trackRecognitionsNavigate({
        filter: `profileRecognitionsTab-${filterType}`,
      })
      if (filterType === 'all') this.filters = {}
      if (filterType === 'given')
        this.filters = { createdBy: this.profileAccount.id }
      if (filterType === 'received')
        this.filters = { accounts: [this.profileAccount.id] }

      this.fetchRecognitions()
    },
    async handlePageChange(page) {
      this.currentPage = page
      await this.fetchRecognitions()
    },

    async fetchRecognitions() {
      try {
        this.loading = true
        const response = await api.recognition.getAll(this.organizationId, {
          skip: (this.currentPage - 1) * ITEMS_PER_PAGE,
          limit: ITEMS_PER_PAGE,
          filters: this.filters,
          allRecognitionsOf: isEmpty(this.filters)
            ? this.profileAccount.id
            : null,
        })
        this.recognitions = response?.data?.results

        this.totalPages =
          Math.ceil(response?.data?.totalRecordsInData / ITEMS_PER_PAGE) || 0
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.absolute-right {
  position: absolute;
  right: 8px;
  top: 12px;
}
</style>
